import React from 'react';
import { FilterSetController, SelectButton, Filter, Table, ContentCard, Card, DropdownSort, DropdownItem, DropdownLine } from '@weezevent/nacre';
import TemplateEmpty from './template-row/templateEmpty';
import DesignTemplateModel from '../../../../models/designTemplateModel';
import DesignModel from '../../../../models/designModel';
import { TemplateRow } from './template-row/templateRow';
import { translateName } from '../../detail/templateForm';
import { handleSearchQuery } from '../../../manager/components/context';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import Style from './templateList.module.css';
import LoadingTable from '../../../../components/loadingTable';
import { typesKeyNames } from './helper';
import { TemplateModale } from './template-row/templateModale';

function TemplateHeader({ allSelected, ordering, setOrdering }) {
    const { t } = useTranslation();

    const columns = [
        {
            order: 'created',
            label: t('ticket.ticket_design.creation_date'),
            sorting: true
        },
        {
            order: 'name',
            label: t('ticket.ticket_design.templates.name'),
            sorting: true
        },
        {
            order: 'type',
            label: t('ticket.ticket_design.templates.type'),
            sorting: false
        },
        {
            class: 'action',
            label: t('ticket.common.cta.actions'),
            sorting: false,
            colspan: 2
        }
    ];

    return (
        <Table.THead>
            <Table.Tr active={allSelected}>
                {columns.map((column, index) => (
                    <th key={index} colSpan={column.colspan || undefined} className={column.class === 'action' ? Style['action-column'] : null}>
                        {column.sorting ? (
                            <div>
                                <DropdownSort
                                    active={ordering === column.order || ordering === `-${column.order}`}
                                    name={column.order}
                                    ordering={ordering}
                                    label={column.label}
                                    items={[
                                        <DropdownItem.Icons
                                            key={1}
                                            item={t('ticket.common.ordering.descending')}
                                            checked={ordering === `-${column.order}`}
                                            onClick={() => setOrdering(`-${column.order}`)}
                                        />,
                                        <DropdownItem.Icons
                                            key={2}
                                            item={t('ticket.common.ordering.ascending')}
                                            checked={ordering === column.order}
                                            onClick={() => setOrdering(column.order)}
                                        />
                                    ]}
                                />
                            </div>
                        ) : (
                            column.label
                        )}
                    </th>
                ))}
            </Table.Tr>
        </Table.THead>
    );
}

const spec = {
    multiple: ['type__in'],
    fields: ['type__in', 'search'],
    date: ['created']
};

export default function TemplateList({ organization, isReadOnly = true }) {
    const { t } = useTranslation();
    const [ordering, setOrdering] = React.useState('name');
    const [filters, setFilters] = React.useState({});
    const [allSelected, setAllSelected] = React.useState(false);
    const [selectedTemplates, setSelectedTemplates] = React.useState({});
    const [modaleType, setModaleType] = React.useState({ action: null, selected: [] });
    const [templatesIds, setTemplatesIds] = React.useState(null);

    const numberSelected = React.useMemo(() => Object.keys(selectedTemplates)?.length || 0, [selectedTemplates]);

    const typeOptions = Object.entries(typesKeyNames).map(([key, value]) => {
        return {
            key: key,
            name: key,
            text: t(`ticket.ticket_design.type.${value}`)
        };
    });

    const resetSelected = React.useCallback(() => {
        setAllSelected(false);
        setSelectedTemplates({});
    });

    const [templates, templatesLoading, sync] = DesignTemplateModel.useApiModel(
        {
            organizationId: organization.pk,
            query: {
                ordering: ordering,
                ...handleSearchQuery(spec, filters, false)
            },
            onSuccess: _templates => {
                // on first GET while there isn't any filters we get all template's linked designs
                if (!Object.keys(filters).length && !templatesIds) {
                    setTemplatesIds(_templates.map(t => t.pk));
                }
            }
        },
        [organization, ordering, filters]
    );

    const [designs] = DesignModel.useApiModel(
        {
            organizationId: organization.pk,
            query: {
                template__in: templatesIds
            },
            launch: Boolean(templatesIds)
        },
        [organization, templatesIds]
    );

    const onChangeSearch = React.useCallback(
        value => {
            let tmpFilters = { ...filters };
            if (value !== '' && value !== null) {
                tmpFilters = { ...tmpFilters, search: value };
            } else {
                delete tmpFilters.search;
            }
            setFilters(tmpFilters);
        },
        [filters]
    );

    const handleAllClick = React.useCallback(
        ({ event, data: { checked } }) => {
            let templatesTmp = {};
            if (checked) {
                templates.forEach(template => {
                    templatesTmp[template.pk] = template;
                });
            }
            setAllSelected(checked);
            setSelectedTemplates(templatesTmp);
        },
        [templates]
    );

    const handleElementsList = React.useCallback(
        ({ event, data: { checked } }, template) => {
            setSelectedTemplates(currentState => {
                const newSelected = { ...currentState };
                const isSelected = template.pk in currentState;
                if (!checked && isSelected) {
                    delete newSelected[template.pk];
                }

                if (checked && !isSelected) {
                    newSelected[template.pk] = template;
                }
                setAllSelected(Object.values(newSelected).length === templates.length);

                return newSelected;
            });
        },
        [templates]
    );

    return (
        <Card separator={null}>
            <ContentCard>
                <FilterSetController
                    total={templates?.length}
                    isLoadingTotal={templatesLoading}
                    labelTotal={t('ticket.ticket_design.templates.total', { count: templates?.length })}
                    labelReset={t('ticket.common.action.reset')}
                    labelFilters={t('ticket.common.cta.filters')}
                    search={{
                        onChange: (synth, value) => {
                            onChangeSearch(value);
                            resetSelected();
                        },
                        handleReset: (synth, value) => {
                            onChangeSearch(null);
                        },
                        placeholder: t('ticket.ticket_design.templates.search'),
                        value: filters?.search || ''
                    }}
                    onChange={values => {
                        if (filters.search !== undefined && filters.search.length > 0) {
                            values.search = filters.search;
                        }
                        setFilters(values);
                        resetSelected();
                    }}
                    onReset={() => {}}
                    onDelete={key => {}}
                >
                    <Filter
                        name="type__in"
                        multiple
                        labelSelectAll={t('ticket.common.action.select-all')}
                        labelName={t('ticket.ticket_design.templates.type')}
                        options={typeOptions}
                        index="0"
                        labelCTA={t('ticket.common.action.apply')}
                    />
                </FilterSetController>
                <div className={Style['global-pagesize-container']}>
                    <SelectButton
                        disabled={numberSelected === 0}
                        tooltipTitle={t('ticket.ticket_design.templates.selection.no_template')}
                        title={numberSelected > 0 ? t('ticket.ticket_design.templates.selection.action', { count: numberSelected }) : t('ticket.common.action.bulk')}
                        objects={numberSelected > 0 ? Object.values(selectedTemplates) : []}
                    >
                        <div>
                            {numberSelected === 1 && (
                                <>
                                    <DropdownItem onClick={() => setModaleType({ action: 'duplicate', selected: Object.values(selectedTemplates) })}>
                                        {t('ticket.common.action.duplicate')}
                                    </DropdownItem>
                                    <DropdownItem onClick={() => setModaleType({ action: 'preview', selected: Object.values(selectedTemplates) })}>
                                        {t('ticket.ticket_design.templates.modale.preview')}
                                    </DropdownItem>
                                    <DropdownLine />
                                </>
                            )}
                            <DropdownItem deleteItem onClick={() => setModaleType({ action: 'delete', selected: Object.values(selectedTemplates) })}>
                                {t('ticket.common.action.delete')}
                            </DropdownItem>
                        </div>
                    </SelectButton>
                </div>
            </ContentCard>
            {modaleType.action && (
                <TemplateModale
                    designs={designs.filter(d => modaleType.selected.some(template => template.id === d.template_id))}
                    organization={organization}
                    sync={() => sync({ allow_cache: false, cache: false })}
                    setShowModale={setModaleType}
                    templates={modaleType.selected}
                    type={modaleType.action}
                    setSelectedTemplates={setSelectedTemplates}
                />
            )}
            <Table pk="id" useNew selectable onSelectAll={(event, data) => handleAllClick({ event, data })} className={Style['list__table']}>
                <TemplateHeader allSelected={allSelected} ordering={ordering} setOrdering={setOrdering} />
                {!templatesLoading && templates && (
                    <Table.TBody>
                        {templates?.length > 0 &&
                            templates.map((template, idx) => {
                                template.name = translateName(template?.translations?.name, template.name, i18n.accountLocale);
                                return (
                                    <TemplateRow
                                        key={idx}
                                        template={template}
                                        organization={organization}
                                        selectedTemplates={selectedTemplates}
                                        onSelected={handleElementsList}
                                        setModaleType={setModaleType}
                                        isReadOnly={isReadOnly}
                                    />
                                );
                            })}
                    </Table.TBody>
                )}
            </Table>
            <LoadingTable loading={templatesLoading} />
            {!templatesLoading && !templates?.length && <TemplateEmpty isReadOnly={isReadOnly} />}
        </Card>
    );
}
